import React from 'react';
import styled, { keyframes } from 'styled-components';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import emptyRefrigerator from '../assets/empty-refrigerator.svg';

const appear = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  min-height: 100svh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  min-width: 0;
  color: #1a2e40;
`;

const MainArea = styled.main`
  flex: 1;
  min-width: 0;
  display: grid;
  place-items: center;
  padding-block: clamp(32px, 6vw, 72px);
  padding-inline: max(20px, env(safe-area-inset-left, 0px)) max(20px, env(safe-area-inset-right, 0px));
  background:
    radial-gradient(ellipse at 10% 15%, rgba(76, 175, 80, 0.08), transparent 34%),
    #f8faf8;

  @media (max-width: 700px) {
    padding-block: 28px 40px;
  }

  @media (max-width: 360px) {
    padding-block: 22px 32px;
    padding-inline: max(16px, env(safe-area-inset-left, 0px)) max(16px, env(safe-area-inset-right, 0px));
  }

  @media (orientation: landscape) and (max-height: 560px) {
    padding-block: 20px;
  }
`;

const Layout = styled.div`
  width: min(100%, 1040px);
  min-width: 0;
  display: grid;
  grid-template-columns: minmax(0, 1.05fr) minmax(0, 0.95fr);
  align-items: center;
  gap: clamp(40px, 7vw, 80px);
  animation: ${appear} 400ms ease-out both;

  @media (max-width: 820px) {
    grid-template-columns: 1fr;
    gap: clamp(22px, 5vw, 34px);
  }

  @media (orientation: landscape) and (max-height: 560px) and (min-width: 560px) {
    grid-template-columns: minmax(0, 1fr) minmax(180px, 0.72fr);
    gap: clamp(20px, 4vw, 40px);
  }

  @media (orientation: landscape) and (max-height: 420px) and (min-width: 560px) {
    gap: 18px;
  }

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const Content = styled.section`
  min-width: 0;
  max-width: 470px;

  @media (max-width: 820px) {
    grid-row: 2;
    width: 100%;
    justify-self: center;
  }

  @media (orientation: landscape) and (max-height: 560px) and (min-width: 560px) {
    grid-row: auto;
  }

  @media (orientation: landscape) and (max-height: 420px) and (min-width: 560px) {
    max-width: 400px;
  }
`;

const Eyebrow = styled.p`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 0 18px;
  color: #4b8150;
  font-family: 'Roboto', sans-serif;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;

  &::before {
    width: 26px;
    height: 2px;
    background: #4caf50;
    content: '';
  }

  @media (forced-colors: active) {
    color: CanvasText;

    &::before {
      background: CanvasText;
    }
  }
`;

const Title = styled.h1`
  margin: 0;
  color: #1a2e40;
  font-family: 'Poppins', sans-serif;
  font-size: clamp(2.15rem, 4vw, 3rem);
  font-weight: 600;
  letter-spacing: -0.045em;
  line-height: 1.16;
  text-wrap: balance;

  @media (max-width: 820px) {
    font-size: clamp(2rem, 6vw, 2.7rem);
  }

  @media (max-width: 360px) {
    font-size: clamp(1.8rem, 8vw, 2.15rem);
    letter-spacing: -0.035em;
  }

  @media (orientation: landscape) and (max-height: 560px) and (min-width: 560px) {
    font-size: clamp(1.75rem, 4vw, 2.4rem);
  }

  @media (orientation: landscape) and (max-height: 420px) and (min-width: 560px) {
    font-size: clamp(1.6rem, 3.4vw, 2rem);
  }
`;

const Description = styled.p`
  max-width: 420px;
  margin: 18px 0 0;
  color: #5f6f79;
  font-family: 'Roboto', sans-serif;
  font-size: clamp(1rem, 1.3vw, 1.1rem);
  line-height: 1.7;
  overflow-wrap: anywhere;

  @media (orientation: landscape) and (max-height: 560px) and (min-width: 560px) {
    margin-top: 12px;
    font-size: 1rem;
    line-height: 1.55;
  }
`;

const Illustration = styled.div`
  position: relative;
  width: 100%;
  max-width: 400px;
  min-width: 0;
  aspect-ratio: 1.08;
  display: grid;
  place-items: center;
  justify-self: end;
  overflow: hidden;
  border: 1px solid #e7eee8;
  border-radius: 24px;
  background: radial-gradient(ellipse at 48% 52%, #fbfdfb 0%, #f0f6f1 68%, #eaf2eb 100%);

  @media (max-width: 820px) {
    width: min(100%, 380px);
    justify-self: center;
    grid-row: 1;
    aspect-ratio: 1.2;
  }

  @media (orientation: landscape) and (max-height: 560px) and (min-width: 560px) {
    width: 100%;
    max-width: 250px;
    aspect-ratio: 1.08;
    justify-self: end;
    grid-row: auto;
  }

  @media (orientation: landscape) and (max-height: 420px) and (min-width: 560px) {
    max-width: 190px;
  }

  @media (forced-colors: active) {
    border-color: CanvasText;
    background: Canvas;
  }
`;

const FridgeIllustration = styled.img`
  position: relative;
  z-index: 1;
  display: block;
  width: min(92%, 370px);
  height: auto;
  max-height: 100%;
  object-fit: contain;

  @media (orientation: landscape) and (max-height: 560px) and (min-width: 560px) {
    width: min(92%, 230px);
  }

  @media (orientation: landscape) and (max-height: 420px) and (min-width: 560px) {
    width: min(92%, 175px);
  }
`;

const NotFoundPage = () => (
  <Page>
    <Header />
    <MainArea>
      <Layout>
        <Content aria-labelledby="not-found-title">
          <Eyebrow>Erreur 404</Eyebrow>
          <Title id="not-found-title">Cette page est introuvable.</Title>
          <Description>
            L’adresse est peut-être incorrecte, ou la page a été déplacée ou supprimée.
          </Description>
        </Content>

        <Illustration aria-hidden="true">
          <FridgeIllustration src={emptyRefrigerator} alt="" />
        </Illustration>
      </Layout>
    </MainArea>
    <Footer />
  </Page>
);

export default NotFoundPage;
